<template>
  <div>
    <b-card no-body>
      <b-card-header class="pb-50">
        <div class="d-flex justify-content-between align-items-center w-100">
          <h5 class="mb-0">
            Пакеты<span v-if="total !== null" class="text-muted font-small-3 ml-50">({{ total }})</span>
          </h5>
          <b-button
              v-if="hasActiveFilters"
              variant="outline-danger"
              size="sm"
              @click="resetFilters"
          >
            <feather-icon icon="XIcon" class="mr-25" />
            Сбросить
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Название</label>
            <b-input-group>
              <b-form-input
                  v-model="searchQuery"
                  placeholder="Поиск по названию..."
                  @keyup.enter="applyFilters()"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="applyFilters()">
                  Применить
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Тип</label>
            <v-select
                v-model="filters.type"
                :options="typeOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="applyFilters()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Статус</label>
            <v-select
                v-model="filters.status"
                :options="statusOptions"
                :reduce="o => o.value"
                :clearable="true"
                placeholder="Все"
                @input="applyFilters()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Архив</label>
            <b-form-radio-group
                v-model="filters.is_archived"
                :options="triStateOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                @change="applyFilters()"
            />
          </b-col>
          <b-col cols="6" md="2" class="mb-1">
            <label class="d-block font-small-3 text-muted mb-25">Ошибки</label>
            <b-form-radio-group
                v-model="filters.has_errors"
                :options="triStateOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                @change="applyFilters()"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-table
          :items="packsList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          empty-text="Ничего не найдено"
          class="position-relative"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          no-local-sorting
          :busy="loading"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Загрузка...</strong>
          </div>
        </template>

        <template #cell(name)="data">
          <b-link
              :to="{ name: 'generator-pack-detail', params: { id: data.item.id } }"
              class="font-weight-bold"
          >
            {{ data.item.name }}
          </b-link>
        </template>

        <template #cell(type)="data">
          {{ packTypes[data.item.type] || data.item.type }}
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(user_id)="data">
          <template v-if="data.item.user">
            <div>{{ data.item.user.name }}</div>
            <small class="text-muted">{{ data.item.user.email }}</small>
          </template>
          <span v-else>—</span>
        </template>

        <template #cell(has_errors)="data">
          <feather-icon
              v-if="data.item.has_errors"
              icon="AlertTriangleIcon"
              class="text-danger"
          />
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(is_archived)="data">
          <b-badge v-if="data.item.is_archived" variant="light-secondary">Архив</b-badge>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <template #cell(updated_at)="data">
          {{ formatDate(data.item.updated_at) }}
        </template>

        <template #cell(actions)="data">
          <b-button
              v-if="data.item.has_file"
              size="sm"
              variant="outline-primary"
              title="Скачать файл пакета"
              @click="downloadPack(data.item)"
          >
            <feather-icon icon="DownloadIcon" />
          </b-button>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <label class="text-nowrap mb-0 mr-50 font-small-3">На странице:</label>
          <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              style="min-width: 80px"
              @input="applyFilters()"
          />
        </div>
        <b-pagination
            v-if="total > 0"
            v-model="page"
            :total-rows="total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0"
            prev-class="prev-item"
            next-class="next-item"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BFormInput,
  BFormRadioGroup,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BPagination,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import store from '@/store'
import { computed, onUnmounted, ref, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import generatorStoreModule from '@/views/generator/generatorStoreModule'
import { PACK_STATUSES, PACK_TYPES } from '@/views/generator/packDictionaries'

const TRI_STATE_OPTIONS = [
  { text: 'Все', value: null },
  { text: 'Да', value: '1' },
  { text: 'Нет', value: '0' },
]

function getDefaultFilters() {
  return {
    type: null,
    status: null,
    is_archived: null,
    has_errors: null,
  }
}

const FILTER_KEYS = Object.keys(getDefaultFilters())

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BFormInput,
    BFormRadioGroup,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    vSelect,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-generator'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, generatorStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    // --- State ---
    const page = ref(1)
    const perPage = ref(20)
    const searchQuery = ref(null)
    const total = ref(null)
    const sortBy = ref('id')
    const isSortDirDesc = ref(true)
    const loading = ref(false)
    const filters = ref(getDefaultFilters())
    const packsList = ref([])

    const perPageOptions = [20, 30, 50, 100]

    // --- Read URL query on init ---
    const route = root.$route
    if (route.query.q) searchQuery.value = route.query.q
    if (route.query.sort_by) sortBy.value = route.query.sort_by
    if (route.query.sort_desc !== undefined) isSortDirDesc.value = route.query.sort_desc === '1' || route.query.sort_desc === 'true'
    if (route.query.per_page) perPage.value = parseInt(route.query.per_page, 10) || 20
    if (route.query.page) page.value = parseInt(route.query.page, 10) || 1

    FILTER_KEYS.forEach(key => {
      if (route.query[key] !== undefined && route.query[key] !== '') {
        filters.value[key] = route.query[key]
      }
    })

    const hasActiveFilters = computed(() => {
      const currentFilters = filters.value
      return (searchQuery.value !== null && searchQuery.value !== '')
        || FILTER_KEYS.some(key => currentFilters[key] !== null && currentFilters[key] !== '' && currentFilters[key] !== undefined)
    })

    // --- Table columns (ключи сортируемых колонок = поля sort_by на бэке) ---
    const tableColumns = [
      { key: 'id', label: '#', sortable: true },
      { key: 'name', label: 'Название', sortable: true },
      { key: 'type', label: 'Тип', sortable: true },
      { key: 'status', label: 'Статус', sortable: true },
      { key: 'user_id', label: 'Пользователь', sortable: true },
      { key: 'has_errors', label: 'Ошибки', sortable: false },
      { key: 'is_archived', label: 'Архив', sortable: false },
      { key: 'created_at', label: 'Создан', sortable: true },
      { key: 'updated_at', label: 'Обновлён', sortable: true },
      { key: 'actions', label: '' },
    ]

    const typeOptions = Object.keys(PACK_TYPES).map(value => ({ value, label: PACK_TYPES[value] }))
    const statusOptions = Object.keys(PACK_STATUSES).map(value => ({ value, label: PACK_STATUSES[value].label }))

    // --- API ---
    const buildQueryParams = () => {
      const params = {
        q: searchQuery.value || undefined,
        page: page.value,
        per_page: perPage.value,
        sort_by: sortBy.value,
        sort_desc: isSortDirDesc.value,
      }
      const currentFilters = filters.value
      FILTER_KEYS.forEach(key => {
        if (currentFilters[key] !== null && currentFilters[key] !== '' && currentFilters[key] !== undefined) {
          params[key] = currentFilters[key]
        }
      })
      return params
    }

    const syncUrlQuery = () => {
      const params = buildQueryParams()
      const query = {}
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined) {
          query[key] = String(params[key])
        }
      })
      const currentQuery = root.$route.query
      const isSame = Object.keys(query).length === Object.keys(currentQuery).length
        && Object.keys(query).every(key => currentQuery[key] === query[key])
      if (!isSame) {
        root.$router.replace({ query }).catch(() => {})
      }
    }

    const fetchPacksList = () => {
      const params = buildQueryParams()
      syncUrlQuery()
      loading.value = true
      store.dispatch('app-generator/fetchPacks', params)
        .then(response => {
          packsList.value = response.data.data
          total.value = response.data.meta.total
        })
        .finally(() => {
          loading.value = false
        })
    }

    const applyFilters = () => {
      page.value = 1
      fetchPacksList()
    }

    const resetFilters = () => {
      filters.value = getDefaultFilters()
      searchQuery.value = null
      applyFilters()
    }

    const downloadPack = item => {
      store.dispatch('app-generator/downloadPack', { id: item.id })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${item.name} [${item.id}].siq`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
    }

    const statusLabel = status => (PACK_STATUSES[status] ? PACK_STATUSES[status].label : status)
    const statusVariant = status => (PACK_STATUSES[status] ? PACK_STATUSES[status].variant : 'light-secondary')
    const formatDate = value => (value ? new Date(value).toLocaleString('ru-RU') : '—')

    // --- Watchers ---
    watch([page, sortBy, isSortDirDesc], () => {
      fetchPacksList()
    })

    // Initial fetch
    fetchPacksList()

    return {
      page,
      perPage,
      perPageOptions,
      searchQuery,
      total,
      sortBy,
      isSortDirDesc,
      loading,
      filters,
      packsList,
      tableColumns,
      hasActiveFilters,

      typeOptions,
      statusOptions,
      triStateOptions: TRI_STATE_OPTIONS,
      packTypes: PACK_TYPES,

      fetchPacksList,
      applyFilters,
      resetFilters,
      downloadPack,
      statusLabel,
      statusVariant,
      formatDate,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
